import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, errorMessage } from "../api/client";

export type Plan = { code: string; name: string; description: string; tier: number; is_addon: boolean; currency: string; monthly_price: string; annual_price: string; trial_days: number; included_users: number; included_sites: number; storage_gb: number; api_requests_per_day: number; per_seat_monthly_price: string; modules: string[]; features: string[] };
export type Subscription = { id: string; plan: Plan; addons: Plan[]; status: string; interval: "month" | "year"; seats: number; trial_ends_at: string | null; trial_days_left: number | null; current_period_start: string | null; current_period_end: string | null; cancel_at_period_end: boolean; canceled_at: string | null; grace_until: string | null; is_read_only: boolean; payment_method: { brand?: string; last4?: string; exp?: string }; billing_email: string; usage: { users: number; sites: number; api_requests_today: number }; limits: Record<string, number>; created_at: string };
export type Invoice = { id: string; number: string; status: string; total: number; currency: string; created: number; hosted_invoice_url: string; invoice_pdf: string };

async function unwrap<T>(p: Promise<{ data?: unknown; error?: unknown }>): Promise<T> {
  const { data, error } = await p;
  if (error) throw new Error(errorMessage(error));
  return data as T;
}

export function usePlans() {
  return useQuery({ queryKey: ["plans"], staleTime: 300_000, queryFn: () => unwrap<Plan[]>(api.GET("/api/v1/billing/plans/")) });
}
export function useSubscription() {
  return useQuery({ queryKey: ["subscription"], queryFn: () => unwrap<Subscription>(api.GET("/api/v1/billing/subscription/")) });
}
export function useInvoices() {
  return useQuery({ queryKey: ["invoices"], queryFn: () => unwrap<Invoice[]>(api.GET("/api/v1/billing/subscription/invoices/")) });
}
export function useHistory() {
  return useQuery({ queryKey: ["billing-history"], queryFn: () => unwrap<{ kind: string; data: Record<string, unknown>; created_at: string }[]>(api.GET("/api/v1/billing/subscription/history/")) });
}
function useRefresh() {
  const qc = useQueryClient();
  return () => { void qc.invalidateQueries({ queryKey: ["subscription"] }); void qc.invalidateQueries({ queryKey: ["me"] }); void qc.invalidateQueries({ queryKey: ["billing-history"] }); };
}
export function useCheckout() {
  return useMutation({ mutationFn: (body: { plan: string; interval: "month" | "year"; seats?: number; addons: string[] }) => unwrap<{ url: string }>(api.POST("/api/v1/billing/subscription/checkout/", { body: body as never })) });
}
export function usePortal() {
  return useMutation({ mutationFn: () => unwrap<{ url: string }>(api.POST("/api/v1/billing/subscription/portal/")) });
}
export function useChange() {
  const refresh = useRefresh();
  return useMutation({ mutationFn: (body: { plan?: string; interval?: "month" | "year"; seats?: number; addons?: string[] }) => unwrap<Subscription>(api.POST("/api/v1/billing/subscription/change/", { body: body as never })), onSuccess: refresh });
}
export function useCancel() {
  const refresh = useRefresh();
  return useMutation({ mutationFn: (body: { reason: string; immediately?: boolean }) => unwrap<Subscription>(api.POST("/api/v1/billing/subscription/cancel/", { body: body as never })), onSuccess: refresh });
}
export function useReactivate() {
  const refresh = useRefresh();
  return useMutation({ mutationFn: () => unwrap<Subscription>(api.POST("/api/v1/billing/subscription/reactivate/")), onSuccess: refresh });
}
export function useBillingEmail() {
  const refresh = useRefresh();
  return useMutation({ mutationFn: (billing_email: string) => unwrap<Subscription>(api.PATCH("/api/v1/billing/subscription/", { body: { billing_email } as never })), onSuccess: refresh });
}
